import type { Set } from '../types/Set'

type Props = {
  set: Set
  onSelect?: (set: Set) => void
}

export default function SetCard({ set, onSelect }: Props) {
  const keywordPreview = (set.keywords || []).slice(0, 3)
  const legoProductUrl = set.id ? `https://www.lego.com/en-us/product/${encodeURIComponent(set.id)}` : undefined
  const rebrickableUrl =
    set.id ? (set.productUrl && set.productUrl.includes('rebrickable.com') ? set.productUrl : `https://rebrickable.com/sets/${set.id}/`) : undefined

  return (
    <article
      className="set-card"
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : undefined}
      onClick={() => onSelect?.(set)}
      onKeyDown={(e) => {
        if (onSelect && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          onSelect(set)
        }
      }}
    >
      <div className="set-card-media">
        {set.image ? (
          <img
            src={set.image}
            alt={set.name}
            loading="lazy"
            onError={(e) => {
              const targ = e.currentTarget as HTMLImageElement
              targ.onerror = null
              targ.src = '/images/placeholder.png'
            }}
          />
        ) : (
          <div className="image-placeholder">No image</div>
        )}
      </div>
      <div className="set-card-body">
        <div className="set-card-meta">
          <h3 className="set-card-title">{set.name}</h3>
          <hr className="set-card-title-hr" />
          <div className="meta-row">
            <span className="set-number left">
              {set.parts ? <>{set.parts.toLocaleString()} pcs</> : 'Pieces n/a'}
            </span>
            <span className="set-number right">
              {set.id && <>#{set.id}</>}
            </span>
          </div>
          <div className="meta-chips">
            {Array.isArray(set.category)
              ? set.category.map((cat) => <span key={cat} className="category-pill">{cat}</span>)
              : set.category && <span className="category-pill">{set.category}</span>}
            {set.ageRange && <span className="chip chip-light">{set.ageRange}</span>}
          </div>
        </div>
        {set.location && <p className="location">Stored in {set.location}</p>}
        {keywordPreview.length > 0 && (
          <div className="set-card-tags" aria-label="Keywords">
            {keywordPreview.map((keyword) => (
              <span key={keyword} className="tag">
                {keyword}
              </span>
            ))}
            {set.keywords && set.keywords.length > keywordPreview.length && (
              <span className="tag muted">+{set.keywords.length - keywordPreview.length} more</span>
            )}
          </div>
        )}
      </div>
      <div className="set-card-footer">
        {set.instructionsUrl && (
          <a
            href={set.instructionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="category-pill"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.18rem' }}
          >
            <LinkIcon /> Manual
          </a>
        )}
        {legoProductUrl && (
          <a
            href={legoProductUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="category-pill"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.18rem' }}
          >
            <LinkIcon /> Lego
          </a>
        )}
        {rebrickableUrl && (
          <a
            href={rebrickableUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="category-pill"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.18rem' }}
          >
            <LinkIcon /> Rebrickable
          </a>
        )}
        {set.instructionsPdf && (
          <a
            href={set.instructionsPdf}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="category-pill"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.18rem' }}
          >
            <LinkIcon /> PDF
          </a>
        )}
      </div>
    </article>
  )
}

function LinkIcon() {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="link-icon"
    >
      <path d="M14 3h7v7" />
      <path d="M10 14 21 3" />
      <path d="M21 14v7H3V3h7" />
    </svg>
  )
}
